import React, { useState, useContext } from 'react';
import { Menu, Layout, Avatar, Row, Col, Button, Dropdown, Card, Typography, Divider } from 'antd';
import { UserOutlined, MenuOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { withUser } from '../../common/contexts/UserContext';
import useNoScroll from '../../common/hooks/useNoScroll';
import style from './PublicLayout.module.less';
import LogoutButton from '../../common/components/LogoutButton';
import NavLink from '../../common/components/NavLink';
import Footer from '../../common/components/Footer';
import useDocumentScrollThrottled from '../../../utils/useDocumentScrollThrottled';
import logoWhite from '../../../images/logo-white.png';
import logoDark from '../../../images/logo-dark.png';
import { FOOTER_LINKS } from '../../../config/constants/constants';
import CartSummary from '../../profile/components/cart/CartSummary';
import CartContext from '../../common/contexts/CartContext';

const { Content, Header } = Layout;
const { Title } = Typography;

const PublicLayout = props => {
  const { children, userContext, path } = props;
  const [siderOpen, setSiderOpen] = useState(false);
  const [fixedHeader, setFixedHeader] = useState(false);

  useNoScroll(siderOpen);

  useDocumentScrollThrottled(callbackData => {
    const { currentScrollTop } = callbackData;

    setFixedHeader(currentScrollTop > 2);
  });

  const { cart } = useContext(CartContext);

  // Define menu items separately, so they can be put in both the mobile and desktop menu
  const menuChildren = [
    <NavLink route="/camps" key="camps">
      <FormattedMessage defaultMessage="Find A Campground" />
    </NavLink>,
    <NavLink route="/events" key="events">
      <FormattedMessage defaultMessage="Find Nearby Events" />
    </NavLink>,
    <Menu.Item key="regions">
      <Dropdown
        placement="bottomRight"
        arrow
        overlay={
          <div>
            <Card className={style.regions}>
              <Title level={5}>
                <FormattedMessage defaultMessage="Provinces" />
              </Title>
              <Row>
                <Col span={12}>
                  <Menu selectable>
                    <NavLink route="/alberta" key="alberta">
                      <FormattedMessage defaultMessage="Alberta" />
                    </NavLink>
                    <NavLink route="/british-columbia" key="british-columbia">
                      <FormattedMessage defaultMessage="British Columbia" />
                    </NavLink>
                    <NavLink route="/manitoba" key="manitoba">
                      <FormattedMessage defaultMessage="Manitoba" />
                    </NavLink>
                    <NavLink route="/new-brunswick" key="new-brunswick">
                      <FormattedMessage defaultMessage="New Brunswick" />
                    </NavLink>
                    <NavLink route="/newfoundland-and-labrador" key="newfoundland-and-labrador">
                      <FormattedMessage defaultMessage="Newfoundland & Labrador" />
                    </NavLink>
                  </Menu>
                </Col>
                <Col span={12}>
                  <Menu selectable>
                    <NavLink route="/nova-scotia" key="nova-scotia">
                      <FormattedMessage defaultMessage="Nova Scotia" />
                    </NavLink>
                    <NavLink route="/ontario" key="ontario">
                      <FormattedMessage defaultMessage="Ontario" />
                    </NavLink>
                    <NavLink route="/quebec" key="quebec">
                      <FormattedMessage defaultMessage="Quebec" />
                    </NavLink>
                    <NavLink route="/saskatchewan" key="saskatchewan">
                      <FormattedMessage defaultMessage="Saskatchewan" />
                    </NavLink>
                  </Menu>
                </Col>
              </Row>
              <Divider />
              <Row>
                <Col span={12}>
                  <Menu selectable>
                    <NavLink route="/united-states" key="united-states">
                      <FormattedMessage defaultMessage="United States" />
                    </NavLink>
                  </Menu>
                </Col>
              </Row>
            </Card>
          </div>
        }
      >
        <Button type="text" className={style.regionButton}>
          <FormattedMessage defaultMessage="Camping Destinations" />
        </Button>
      </Dropdown>
    </Menu.Item>,
    <NavLink route={FOOTER_LINKS.faq} key="faq" externalLink>
      <FormattedMessage defaultMessage="FAQ" />
    </NavLink>,
    userContext.user // Menu items for users that are logged in
      ? [
          userContext.user.type === 'admin' ? (
            <NavLink key="adminPortal" route="/admin">
              <FormattedMessage defaultMessage="Admin Portal" />
            </NavLink>
          ) : null,
          userContext.user.type === 'client' ? (
            <NavLink key="clientPortal" route="/client">
              <FormattedMessage defaultMessage="Client Portal" />
            </NavLink>
          ) : null,
          <Menu.Item key="cart" className={style.cartWrapper}>
            <CartSummary className={style.cart} />
          </Menu.Item>,
          <Menu.SubMenu
            key="userMenu"
            title={
              <>
                <div className={style.userIcon}>
                  <Avatar icon={<UserOutlined />} />
                </div>
                <div className={style.profileTitle}>
                  <FormattedMessage defaultMessage="Profile" />
                </div>
              </>
            }
          >
            <NavLink key="profile" route="/profile">
              <FormattedMessage defaultMessage="Profile" />
            </NavLink>

            <NavLink key="bookings" route="/profile/bookings">
              <FormattedMessage defaultMessage="My Bookings" />
            </NavLink>

            <NavLink key="event-registrations" route="/profile/event-registrations">
              <FormattedMessage defaultMessage="My Events" />
            </NavLink>

            <Menu.Item key="logout">
              <LogoutButton />
            </Menu.Item>
          </Menu.SubMenu>,
        ]
      : [
          // Menu Items for users not logged in
          <NavLink route="/login" key="login">
            <FormattedMessage defaultMessage="Login" />
          </NavLink>,
          <NavLink route="/register" key="register">
            <Button type="primary" className={style.register}>
              <FormattedMessage defaultMessage="Sign Up" />
            </Button>
          </NavLink>,
        ],
  ];

  let useAltMenu = false;
  const altMenuPages = [
    '/alberta',
    '/british-columbia',
    '/manitoba',
    '/new-brunswick',
    '/nova-scotia',
    '/ontario',
    '/quebec',
    '/saskatchewan',
    '/',
  ];

  if (altMenuPages.indexOf(path) !== -1) {
    useAltMenu = true;
  }
  const headerStyle = useAltMenu ? style.homeHeader : style.header;
  const fixedStyle = useAltMenu && fixedHeader ? style.header : '';
  const logo = useAltMenu && !fixedHeader ? logoWhite : logoDark;

  return (
    <Layout className={style.publicLayout}>
      <Header className={`${headerStyle} ${fixedStyle}`}>
        <Row>
          <Col xs={8} sm={12} lg={6} className={style.logoColumn}>
            <Link to="/">
              <img className={style.logo} src={logo} alt="Let's Camp" />
            </Link>
          </Col>
          <Col xs={0} lg={18} xl={18} className={style.menuColumn}>
            <Menu mode="horizontal" className={style.navMenu}>
              {menuChildren}
            </Menu>
          </Col>
          <Col xs={16} sm={12} lg={0} style={{ textAlign: 'right' }}>
            {Boolean(cart?.numItems) && (
              <div style={{ display: 'inline-block' }} className="margin-right-md">
                <CartSummary className={style.cart} noTimer />
              </div>
            )}

            <Button icon={<MenuOutlined />} onClick={() => setSiderOpen(!siderOpen)} />
          </Col>
        </Row>
      </Header>
      <div
        className={`${style.slidingMenu} ${useAltMenu && !fixedHeader ? style.homeSlidingMenu : ''} ${siderOpen ? 'active' : ''}`}
      >
        <Menu mode="inline">{menuChildren}</Menu>
      </div>
      <Content className={style.content}>
        <div>{children}</div>
      </Content>
      <Footer />
    </Layout>
  );
};

export default withUser(PublicLayout);
